<motn-drawer-gap-from-top
  (clickedOutside$)="closed$.emit()"
>
  <h3>Create Numeric Tile</h3>

  <div class="content">
    <div class="section">
      <h2>Data Source</h2>

      <div class="data-source-options">
        <div
          class="data-source-option"
          (click)="onDataSourceChange(TileDataSource.Device)"
        >
          <div class="check">
            @if (dataSource() === TileDataSource.Device) {
              <span><i class="fa-solid fa-circle-check"></i></span>
            } @else {
              <span><i class="fa-regular fa-circle"></i></span>
            }
          </div>
          <div class="radio-content">
            <span class="radio-label">Use device data</span>
            <span class="radio-desc">Show value from a selected device</span>
          </div>
        </div>

        <div
          class="data-source-option"
          (click)="onDataSourceChange(TileDataSource.Formula)"
        >
          <div class="check">
            @if (dataSource() === TileDataSource.Formula) {
              <span><i class="fa-solid fa-circle-check"></i></span>
            } @else {
              <span><i class="fa-regular fa-circle"></i></span>
            }
          </div>
          <div class="radio-content">
            <span class="radio-label">Use formula</span>
            <span class="radio-desc">Calculate value using a formula</span>
          </div>
        </div>
      </div>

      @if (dataSource() === TileDataSource.Device) {
        <div class="device-config">
          <div class="field">
            <div class="device-selector">
              <button
                class="select-trigger"
                (click)="deviceSelectorOpen.set(!deviceSelectorOpen())"
              >
                <span>{{ device().name || 'Select a device' }}</span>
                <i class="fa-regular fa-chevron-down"></i>
              </button>

              @if (deviceSelectorOpen()) {
                <motn-popover-select-devices
                  [preselectedDevices]="[]"
                  [multiOrSingle]="'single'"
                  [allowedTypes]="allowedDeviceTypes"
                  [overlayPositions]="deviceSelectorPositions"
                  (clickedOutsideMenu$)="deviceSelectorOpen.set(false)"
                  (clickedOption$)="onDeviceSelected($event)"
                ></motn-popover-select-devices>
              }
            </div>
            <span class="label">Device Name</span>
          </div>

          <div class="field device-point-field">
            <div class="point-row">
              @if (device().pointName) {
                <div class="point-name-with-value">
                  <span class="point-name">{{ device().pointName }}</span>
                  @if (device().pointValue != null) {
                    <motn-canvas-mini-tile
                      [directValue]="device().pointValue!.value"
                      [directValueTime]="device().pointValue!.time ?? ''"
                      [directValueOffset]="0"
                      [hideIcon]="true"
                      [compact]="true"
                    ></motn-canvas-mini-tile>
                  }
                </div>
              }
              <button
                class="outline-sm"
                [disabled]="!device().id"
                (click)="selectPoint()"
              >
                {{ device().pointName ? 'Change Device Point' : 'Select Device Point' }}
              </button>
            </div>
            <span class="label">Device Point</span>
          </div>
        </div>
      }

      @if (dataSource() === TileDataSource.Formula) {
        <div class="formula-config">
          <div class="formula-row">
            <div class="field">
              <div class="formula-selector">
                <button
                  class="select-trigger"
                  (click)="formulaDropdownOpen.set(!formulaDropdownOpen())"
                >
                  <span>{{ isCustomFormula() ? 'Create Custom Formula' : (formula() ? formula()!.name : 'Select a formula') }}</span>
                  <i class="fa-regular fa-chevron-down"></i>
                </button>

                @if (formulaDropdownOpen()) {
                  <motn-popover-menu
                    [optionGroup]="formulaOptions"
                    [menuTitle]="'Formulas'"
                    (clickedOption$)="onSelectFormula($event)"
                    (clickedOutsideMenu$)="formulaDropdownOpen.set(false)"
                  ></motn-popover-menu>
                }
              </div>
              <span class="label">Formula</span>
            </div>

            @if (isCustomFormula()) {
              <div class="field custom-formula-field">
                <motn-formula-custom-input
                  [variableColors]="variableColors()"
                  (formulaChanged)="onCustomFormulaChanged($event)"
                  (variablesChanged)="onCustomFormulaVariablesChanged($event)"
                  (variableRenamed)="onVariableRenamed($event)"
                  (validationResult)="onCustomFormulaValidationResult($event)"
                ></motn-formula-custom-input>

                <div class="formula-toolbar">
                  <div class="toolbar-group">
                    <div class="toolbar-btn-row">
                      @for (op of operators; track op) {
                        <button
                          type="button"
                          class="toolbar-btn symbol"
                          (mousedown)="$event.preventDefault()"
                          (click)="onInsertText(op)"
                        >{{ op }}</button>
                      }
                    </div>
                    <span class="label">Operators</span>
                  </div>
                  <div class="toolbar-group">
                    <div class="toolbar-btn-row">
                      @for (fn of formulaFunctions; track fn.label) {
                        <button
                          type="button"
                          class="toolbar-btn toolbar-btn--wide symbol"
                          (mousedown)="$event.preventDefault()"
                          (click)="onInsertText(fn.text, fn.cursorBack)"
                        >{{ fn.label }}</button>
                      }
                    </div>
                    <span class="label">Functions</span>
                  </div>
                </div>

                <div class="formula-hint">
                  <i class="fa-solid fa-circle-info"></i>
                  <span>Type a variable name and it will appear below for configuration.</span>
                </div>
              </div>
            }
          </div>

          @if (formula() || (isCustomFormula() && currentVariables().length > 0)) {
            <div class="variables">
              @for (variable of currentVariables(); track variable.key) {
                <div class="variable-col">
                  <div class="variable-card-header">
                    <motn-formula-variable-pill
                      [name]="variable.label"
                      [color]="variableColors()[variable.key] ?? 'red'"
                      [readonly]="true"
                    ></motn-formula-variable-pill>

                    @if (isCustomFormula()) {
                      <div class="variable-kebab">
                        <button
                          class="kebab-trigger"
                          (click)="kebabOpenKey.set(kebabOpenKey() === variable.key ? null : variable.key)"
                        >
                          <i class="fa-solid fa-ellipsis-vertical"></i>
                        </button>
                        @if (kebabOpenKey() === variable.key) {
                          <motn-popover-menu
                            [optionGroup]="deleteMenuOption"
                            (clickedOption$)="deleteVariable(variable.key)"
                            (clickedOutsideMenu$)="kebabOpenKey.set(null)"
                          ></motn-popover-menu>
                        }
                      </div>
                    }
                  </div>

                  <div class="field">
                    <div class="device-selector">
                      <button
                        class="select-trigger"
                        (click)="activeSelectorKey.set(variable.key)"
                      >
                        <span>{{ variableUiState()[variable.key]?.deviceName || 'Select Device' }}</span>
                        <i class="fa-regular fa-chevron-down"></i>
                      </button>

                      @if (activeSelectorKey() === variable.key) {
                        <motn-popover-select-devices
                          [preselectedDevices]="[]"
                          [multiOrSingle]="'single'"
                          [allowedTypes]="formulaDeviceTypes"
                          [overlayPositions]="deviceSelectorPositions"
                          (clickedOutsideMenu$)="activeSelectorKey.set(null)"
                          (clickedOption$)="onVariableDeviceSelected(variable.key, $event)"
                        ></motn-popover-select-devices>
                      }
                    </div>
                    <span class="label">Device Name</span>
                  </div>

                  <div class="field">
                    <div class="point-row">
                      @if (variableUiState()[variable.key]?.pointName) {
                        <div class="point-name-with-value">
                          <span class="point-name">{{ variableUiState()[variable.key]?.pointName }}</span>
                          @if (variableUiState()[variable.key]?.pointValue != null) {
                            <motn-canvas-mini-tile
                              [directValue]="variableUiState()[variable.key]!.pointValue!.value"
                              [directValueTime]="variableUiState()[variable.key]!.pointValue!.time ?? ''"
                              [directValueOffset]="bindings()[variable.key]?.Transformations?.Offset ?? 0"
                              [staleThresholdMinutes]="defaultStaleThreshold"
                              [hideIcon]="true"
                              [compact]="true"
                            ></motn-canvas-mini-tile>
                          }
                        </div>
                      }
                      <button
                        class="outline-sm"
                        [disabled]="!bindings()[variable.key]?.DeviceID"
                        (click)="selectVariablePoint(variable.key)"
                      >
                        {{ variableUiState()[variable.key]?.pointName ? 'Change Device Point' : 'Select Point' }}
                      </button>
                    </div>
                    <span class="label">Device Point</span>
                  </div>

                  <div class="variable-extras">
                    <div class="field">
                      <select
                        [value]="bindings()[variable.key]?.Transformations?.Multiplier ?? ''"
                        (change)="onMultiplierChange(variable.key, $any($event.target).value)"
                      >
                        <option value="">—</option>
                        @for (opt of multiplierOptions; track opt.id) {
                          <option [value]="opt.id">{{ opt.name }}</option>
                        }
                      </select>
                      <span class="label">Multiplier</span>
                    </div>

                    <div class="field">
                      <select
                        [value]="bindings()[variable.key]?.Transformations?.Conversion ?? ''"
                        (change)="onConversionChange(variable.key, $any($event.target).value)"
                      >
                        @for (opt of conversionOptions; track opt.id) {
                          <option [value]="opt.id">{{ opt.name }}</option>
                        }
                      </select>
                      <span class="label">Conversion</span>
                    </div>

                    <div class="field">
                      <input

                        type="number"
                        [value]="bindings()[variable.key]?.Transformations?.Offset ?? 0"
                        (change)="onCalibrationOffsetChange(variable.key, $any($event.target).value)"
                      >
                      <span class="label">Calibration Offset</span>
                    </div>
                  </div>
                </div>
              }
            </div>
          }
        </div>
      }

    </div>

    <div class="actions">
      <button
        class="blue"
        [class.loading]="loading()"
        [disabled]="!canProceed() || loading()"
        (click)="next()"
      >
        <span>Next</span>
        <motn-spinner></motn-spinner>
      </button>


      <button
        class="alt"
        [disabled]="loading()"
        (click)="closed$.emit()"
      >
        Close
      </button>
    </div>
  </div>
</motn-drawer-gap-from-top>

@if (pointSelectorDevice()) {
  <motn-device-point-selector
    [device]="pointSelectorDevice()!"
    (pointSelected$)="onPointSelected($event)"
    (closed$)="pointSelectorDevice.set(null)"
  ></motn-device-point-selector>
}
